import axios from 'axios'
import {
  ModeleSavModeleMessage,
  ModeleSavParametre,
  ModeleSavReferentiel,
  SavControleItem,
  SavControleSaisie,
  SavDetailResponse,
  SavDoublon,
  SavFiltres,
  SavLigneVente,
  SavMessagePrepare,
  SavModelesResponse,
  SavListeResponse,
  SavOrigineResultat,
  SavPieceDecrite,
  SavReferentielAdminResponse,
  SavReceptionItem,
  SavReferentiels,
  SavReserves,
  SavReferentielType,
  SavStatutCode,
} from './Models'

const API_URL = process.env.REACT_APP_API_URL
const SAV_BASE_URL = `${API_URL}/sav`

export const getSavReferentielAdmin = async (
  type: SavReferentielType
): Promise<SavReferentielAdminResponse> => {
  const response = await axios.get(`${SAV_BASE_URL}/admin/referentiels/${type}`)
  return response.data
}

export const createSavReferentiel = async (
  type: SavReferentielType,
  valeur: ModeleSavReferentiel
): Promise<ModeleSavReferentiel> => {
  const response = await axios.post(`${SAV_BASE_URL}/admin/referentiels/${type}`, valeur)
  return response.data
}

export const updateSavReferentiel = async (
  type: SavReferentielType,
  id: number,
  valeur: Partial<ModeleSavReferentiel>
): Promise<ModeleSavReferentiel> => {
  const response = await axios.patch(`${SAV_BASE_URL}/admin/referentiels/${type}/${id}`, valeur)
  return response.data
}

export const deleteSavReferentiel = async (type: SavReferentielType, id: number): Promise<void> => {
  await axios.delete(`${SAV_BASE_URL}/admin/referentiels/${type}/${id}`)
}

export const getSavParametres = async (): Promise<Array<ModeleSavParametre>> => {
  const response = await axios.get(`${SAV_BASE_URL}/admin/parametres`)
  return response.data
}

export const updateSavParametre = async (
  cle: string,
  valeur: string
): Promise<Array<ModeleSavParametre>> => {
  const response = await axios.put(`${SAV_BASE_URL}/admin/parametres/${cle}`, {valeur})
  return response.data
}

// Message d'erreur métier renvoyé par l'API SAV (422)
export const savErreur = (e: any, defaut = 'Une erreur est survenue.'): string =>
  e?.response?.data?.message || (e instanceof Error && !axios.isAxiosError(e) ? e.message : defaut)

// Dossiers SAV (lot 4)

const nettoyer = (filtres: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(filtres).filter(
      ([, v]) => v !== undefined && v !== null && v !== '' && v !== false
    )
  )

export const getSavReferentiels = async (): Promise<SavReferentiels> => {
  const response = await axios.get(`${SAV_BASE_URL}/referentiels`)
  return response.data
}

export const getSavDossiers = async (filtres: SavFiltres, page = 1): Promise<SavListeResponse> => {
  const response = await axios.get(`${SAV_BASE_URL}/dossiers`, {
    params: {...nettoyer(filtres), page},
  })
  return response.data
}

export const getSavDossier = async (id: number | string): Promise<SavDetailResponse> => {
  const response = await axios.get(`${SAV_BASE_URL}/dossiers/${id}`)
  return response.data
}

export const createSavDossier = async (
  donnees: Record<string, any>
): Promise<SavDetailResponse> => {
  const response = await axios.post(`${SAV_BASE_URL}/dossiers`, donnees)
  return response.data
}

export const verifierSavDoublons = async (
  donnees: Record<string, any>
): Promise<Array<SavDoublon>> => {
  const response = await axios.post(`${SAV_BASE_URL}/dossiers/doublons`, donnees)
  return response.data
}

export const updateSavDossier = async (
  id: number,
  donnees: Record<string, any>
): Promise<SavDetailResponse> => {
  const response = await axios.patch(`${SAV_BASE_URL}/dossiers/${id}`, donnees)
  return response.data
}

export const addSavLigne = async (
  id: number,
  ligne: Record<string, any>
): Promise<SavDetailResponse> => {
  const response = await axios.post(`${SAV_BASE_URL}/dossiers/${id}/lignes`, ligne)
  return response.data
}

export const updateSavLigne = async (
  id: number,
  ligneId: number,
  ligne: Record<string, any>
): Promise<SavDetailResponse> => {
  const response = await axios.patch(`${SAV_BASE_URL}/dossiers/${id}/lignes/${ligneId}`, ligne)
  return response.data
}

export const deleteSavLigne = async (id: number, ligneId: number): Promise<SavDetailResponse> => {
  const response = await axios.delete(`${SAV_BASE_URL}/dossiers/${id}/lignes/${ligneId}`)
  return response.data
}

export const transitionSavDossier = async (
  id: number,
  statut: SavStatutCode,
  donnees: {commentaire?: string; motifRefus?: number} = {}
): Promise<SavDetailResponse> => {
  const response = await axios.post(`${SAV_BASE_URL}/dossiers/${id}/transitions`, {
    statut,
    ...donnees,
  })
  return response.data
}

export const derogerSavDossier = async (
  id: number,
  commentaire: string
): Promise<SavDetailResponse> => {
  const response = await axios.post(`${SAV_BASE_URL}/dossiers/${id}/derogation`, {commentaire})
  return response.data
}

export const commenterSavDossier = async (
  id: number,
  message: string,
  visibleClient = false
): Promise<SavDetailResponse> => {
  const response = await axios.post(`${SAV_BASE_URL}/dossiers/${id}/commentaires`, {
    message,
    visibleClient,
  })
  return response.data
}

export const uploadSavDocument = async (
  id: number,
  fichier: File,
  type: string,
  ligne?: number
): Promise<SavDetailResponse> => {
  const formData = new FormData()
  formData.append('fichier', fichier)
  formData.append('type', type)
  if (ligne) formData.append('ligne', String(ligne))
  const response = await axios.post(`${SAV_BASE_URL}/dossiers/${id}/documents`, formData)
  return response.data
}

// Les pièces jointes passent par la route protégée : chargement avec le jeton, puis affichage en blob
export const ouvrirSavDocument = async (documentId: number): Promise<void> => {
  const response = await axios.get(`${SAV_BASE_URL}/documents/${documentId}`, {
    responseType: 'blob',
  })
  const url = URL.createObjectURL(response.data)
  window.open(url, '_blank', 'noopener')
  setTimeout(() => URL.revokeObjectURL(url), 60000)
}

export const rechercherSavOrigine = async (q: string): Promise<Array<SavOrigineResultat>> => {
  const response = await axios.get(`${SAV_BASE_URL}/origines/recherche`, {params: {q}})
  return response.data
}

export const getSavAchatsClient = async (clientId: number): Promise<Array<SavOrigineResultat>> => {
  const response = await axios.get(`${SAV_BASE_URL}/clients/${clientId}/achats`)
  return response.data
}

export const getSavLignesVente = async (
  origine: 'KOENA_CMD' | 'KOENA_B2B',
  id: number
): Promise<Array<SavLigneVente>> => {
  const response = await axios.get(`${SAV_BASE_URL}/origines/${origine}/${id}/lignes`)
  return response.data
}

export const getSavPieceParCode = async (code: string): Promise<SavPieceDecrite | null> => {
  try {
    const response = await axios.get(`${SAV_BASE_URL}/pieces/${encodeURIComponent(code)}`)
    return response.data
  } catch (e: any) {
    if (e?.response?.status === 404) return null
    throw e
  }
}

export const convertirSavClient = async (
  id: number,
  client: number
): Promise<{dossiers: Array<{id: number; numero: string}>}> => {
  const response = await axios.post(`${SAV_BASE_URL}/dossiers/${id}/conversion-client`, {client})
  return response.data
}

// Retour et réception quai (lot 6)

export const organiserSavRetour = async (
  id: number,
  donnees: {transporteur?: number | null; siteReception?: number | null; numeroSuivi?: string}
): Promise<SavDetailResponse> => {
  const response = await axios.post(`${SAV_BASE_URL}/dossiers/${id}/retours`, donnees)
  return response.data
}

export const envoyerSavRetour = async (
  id: number,
  retourId: number,
  commentaire?: string
): Promise<SavDetailResponse> => {
  const response = await axios.post(`${SAV_BASE_URL}/dossiers/${id}/retours/${retourId}/envoi`, {
    commentaire,
  })
  return response.data
}

export const rechercherSavReception = async (q: string): Promise<Array<SavReceptionItem>> => {
  const response = await axios.get(`${SAV_BASE_URL}/receptions/recherche`, {params: {q}})
  return response.data
}

export const getSavRetoursAttendus = async (
  site?: number | null
): Promise<Array<SavReceptionItem>> => {
  const response = await axios.get(`${SAV_BASE_URL}/receptions/attendus`, {
    params: site ? {site} : {},
  })
  return response.data
}

export const receptionnerSavRetour = async (
  retourId: number,
  reserves: SavReserves
): Promise<SavDetailResponse> => {
  const response = await axios.post(`${SAV_BASE_URL}/receptions/${retourId}`, reserves)
  return response.data
}

// Contrôle labo (lot 7)

export const getSavControles = async (site?: number | null): Promise<Array<SavControleItem>> => {
  const response = await axios.get(`${SAV_BASE_URL}/controles`, {params: site ? {site} : {}})
  return response.data
}

export const controlerSavLigne = async (
  ligneId: number,
  saisie: Partial<SavControleSaisie>,
  options: {valider?: boolean; motifRefus?: number | null} = {}
): Promise<SavDetailResponse> => {
  const response = await axios.patch(`${SAV_BASE_URL}/lignes/${ligneId}/controle`, {
    ...saisie,
    ...options,
  })
  return response.data
}

export const getSavDestinationProposee = async (
  fonde: boolean | null,
  reutilisable: boolean | null
): Promise<ModeleSavReferentiel | null> => {
  const params: Record<string, string> = {}
  if (fonde !== null) params.fonde = String(fonde)
  if (reutilisable !== null) params.reutilisable = String(reutilisable)
  const response = await axios.get(`${SAV_BASE_URL}/destinations/proposee`, {params})
  return response.data
}

// Solution et clôture (lot 8)

export const creerSavDemandeRemplacement = async (
  id: number,
  equipe?: number
): Promise<SavDetailResponse> => {
  const response = await axios.post(
    `${SAV_BASE_URL}/dossiers/${id}/demande-remplacement`,
    equipe ? {equipe} : {}
  )
  return response.data
}

// Messages client (lot 9)

export const preparerSavMessage = async (
  id: number,
  evenement: string | null,
  modele?: number | null
): Promise<SavMessagePrepare> => {
  const params: Record<string, any> = {}
  if (evenement) params.evenement = evenement
  if (modele) params.modele = modele
  const response = await axios.get(`${SAV_BASE_URL}/dossiers/${id}/messages/preparer`, {params})
  return response.data
}

export const envoyerSavMessage = async (
  id: number,
  message: {modele?: number | null; evenement?: string | null; objet: string; corps: string}
): Promise<SavDetailResponse> => {
  const response = await axios.post(`${SAV_BASE_URL}/dossiers/${id}/messages`, message)
  return response.data
}

export const getSavModelesMessages = async (): Promise<SavModelesResponse> => {
  const response = await axios.get(`${SAV_BASE_URL}/modeles-messages`)
  return response.data
}

export const createSavModeleMessage = async (
  modele: Record<string, any>
): Promise<ModeleSavModeleMessage> => {
  const response = await axios.post(`${SAV_BASE_URL}/modeles-messages`, modele)
  return response.data
}

export const updateSavModeleMessage = async (
  id: number,
  modele: Record<string, any>
): Promise<ModeleSavModeleMessage> => {
  const response = await axios.put(`${SAV_BASE_URL}/modeles-messages/${id}`, modele)
  return response.data
}

export const apercuSavModeleMessage = async (
  dossier: string,
  objet: string,
  corps: string
): Promise<{
  objet: string
  corps: string
  variablesVides: Array<string>
  dossier: {id: number; numero: string}
}> => {
  const response = await axios.post(`${SAV_BASE_URL}/modeles-messages/apercu`, {
    dossier,
    objet,
    corps,
  })
  return response.data
}

// Envoi automatique d'un message d'événement (création, colis reçu) quand rien ne bloque ; sinon le motif est renvoyé
export const envoyerSavMessageAutomatique = async (
  id: number,
  evenement: string
): Promise<string | null> => {
  const prepare = await preparerSavMessage(id, evenement)
  if (!prepare.modele) return 'aucun modèle actif'
  if (!prepare.destinataire) return "pas d'adresse mail pour ce client"
  if (prepare.variablesVides.length)
    return `variable(s) vide(s) : ${prepare.variablesVides.join(', ')}`
  const reponse = await envoyerSavMessage(id, {
    modele: prepare.modele,
    objet: prepare.objet,
    corps: prepare.corps,
  })
  return reponse.envoi?.statut === 'ECHEC'
    ? "échec d'envoi (une tâche de relance a été créée)"
    : null
}
